import { memo, useMemo } from 'react';
import { css } from '@emotion/react';
import { CallIntelTypes } from '@frontend/api-call-intel';
import { Chart, CustomLegendsData, PieChartData, PieChartProps } from '@frontend/charts';
import { i18next, useTranslation } from '@frontend/i18n';
import { formatters } from '@frontend/shared';
import { theme } from '@frontend/theme';
import { SkeletonLoader, Text } from '@frontend/design-system';
import { useCallIntelDefaultsShallowStore } from '../../hooks/use-call-intel-defaults';
import { CallIntelChip } from '../call-intel-chip';

type Sentiments = Record<CallIntelTypes.SentimentEnum | string, number>;

interface Props {
  isLoading?: boolean;
  sentiments: Sentiments;
  showChartLoader?: boolean;
  onSliceClick?: PieChartProps['onClick'];
}

interface ChartData {
  pie: PieChartData;
  sneakPeak: Array<{ legendId: string; name: string; value: number }>;
  percentages: Record<string, number>;
  values: Record<string, number>;
}

// make sure these three always show, in this order
const DEFAULT_ORDER: CallIntelTypes.SentimentEnum[] = [
  CallIntelTypes.SentimentEnum.SENTIMENT_POSITIVE,
  CallIntelTypes.SentimentEnum.SENTIMENT_NEUTRAL,
  CallIntelTypes.SentimentEnum.SENTIMENT_NEGATIVE,
];

const PIE_APPEARANCE = {
  borderRadius: 4,
  height: 152,
  innerRadius: 58,
  minWidth: 152,
  outerRadius: 75,
};

// Ensure we always render Positive, Neutral, Negative in that order
const orderSentiments = (sentiments: Sentiments = {}): Sentiments =>
  DEFAULT_ORDER.reduce<Sentiments>((acc, key) => {
    acc[key] = sentiments[key] ?? 0;
    return acc;
  }, {});

// Build the pie groups, sneak-peek entries, and percentage lookup
const buildChartData = (sentiments: Sentiments = {}, withEmoji: Record<string, string> = {}): ChartData => {
  const ordered = orderSentiments(sentiments);
  const total = Object.values(ordered).reduce((sum, v) => sum + v, 0) || 1;
  const centerLabel = {
    primaryText: {
      label: i18next.t('Analyzed Calls', {
        ns: 'callIntelInsights',
      }),
      color: theme.colors.neutral50,
      weight: '400',
      textSizeRatio: 1,
    },
    secondaryText: {
      label: (value: number) => formatters.value(value),
      color: theme.colors.neutral50,
      weight: '700',
      textSizeRatio: 1.35,
    },
    textVerticalOffset: -2,
  };

  const data: ChartData = {
    pie: { groups: [], responsiveCenterLabels: centerLabel },
    sneakPeak: [],
    percentages: {},
    values: {},
  };

  Object.entries(ordered).forEach(([id, value]) => {
    data.pie.groups.push({ name: id, value });
    data.sneakPeak.push({ legendId: id, name: withEmoji[id] ?? id, value });
    data.percentages[id] = value / total;
    data.values[id] = value;
  });

  return data;
};

export const SentimentChart = memo(
  ({ isLoading = false, sentiments = {}, showChartLoader = false, onSliceClick }: Props) => {
    const { t } = useTranslation('callIntelInsights');

    const { chipVariants, dataColors, dataLabels } = useCallIntelDefaultsShallowStore(
      'chipVariants',
      'dataColors',
      'dataLabels'
    );

    const isEmptyValue = useMemo(
      () => DEFAULT_ORDER.every((key) => !(sentiments[key] && sentiments[key] > 0)),
      [sentiments]
    );

    const chartData = useMemo(
      () => buildChartData(sentiments ?? {}, dataLabels.sentiments),
      [dataLabels.sentiments, sentiments]
    );

    if (isLoading && !showChartLoader) {
      return <SkeletonLoader height={184} />;
    }

    return (
      <Chart
        css={styles.containerStyles}
        colors={dataColors.sentiments}
        labels={dataLabels.sentiments}
        isLoading={isLoading}
        emptyStateConfig={{ label: t('No data to display.'), style: { height: 127 } }}
      >
        <div css={styles.contentStyles}>
          <Chart.PieChart
            appearance={{
              ...PIE_APPEARANCE,
              tooltipClickHelperText: t('Click to view calls'),
              customTooltipData: (id) => {
                return [
                  {
                    color: dataColors.sentiments?.[id] || '',
                    formattedValue: formatters.value(sentiments[id]),
                    id,
                    label: t('Calls'),
                  },
                ];
              },
            }}
            css={[isEmptyValue && styles.emptyChart]}
            data={sentiments ? chartData.pie : { groups: [] }}
            formatValue={formatters.value}
            onClick={onSliceClick}
          />
          {!isEmptyValue && (
            <Chart.Legends
              allowClickOnCustomLegends
              customData={DEFAULT_ORDER.reduce((acc, sentiment) => {
                acc[sentiment] = {
                  label: (
                    <div css={styles.legendLabel}>
                      <CallIntelChip as='chip' chipVariants={chipVariants} dataKey={sentiment} label='' />
                      <Text size='small' color='subdued'>
                        {dataLabels.sentiments?.[sentiment]}
                      </Text>
                    </div>
                  ),
                  color: dataColors.sentiments?.[sentiment] || '',
                  value: formatters.value(sentiments[sentiment]),
                };
                return acc;
              }, {} as CustomLegendsData)}
              formatValue={formatters.value}
              size='small'
              values={chartData.values}
              style={{ justifyContent: 'flex-start', gap: theme.spacing(2) }}
            />
          )}
        </div>
      </Chart>
    );
  }
);

SentimentChart.displayName = 'SentimentChart';

const styles = {
  containerStyles: css`
    margin-bottom: ${theme.spacing(0)};
    height: 184px;

    > div {
      border-radius: ${theme.borderRadius.small};
      padding: ${theme.spacing(2)};
    }
  `,
  contentStyles: css`
    display: flex;
    flex-direction: row;
    gap: ${theme.spacing(2)};
    align-items: center;
  `,
  emptyChart: css`
    > div {
      min-height: 127px !important;

      p {
        font-size: ${theme.fontSize(12)};
      }
    }
  `,
  legendLabel: css`
    display: flex;
    align-items: center;
    gap: ${theme.spacing(0.5)};
  `,
};
